import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock3,
  ClipboardList,
  ExternalLink,
  Laptop,
  MapPin,
  RefreshCw,
  Sparkles,
  Send,
  Users,
  Video,
  X,
} from "lucide-react";

import {
  Assignment,
  CalendarActivity,
  Cohort,
  programApi,
} from "../api/program";
import MaterialDropzone from "../components/NewView/Common/MaterialDropzone";
import ProgramMaterials from "../components/NewView/Common/ProgramMaterials";

const DAY_MS = 24 * 60 * 60 * 1000;

const activityMeta: Record<
  CalendarActivity["type"],
  {
    label: string;
    icon: typeof Video;
    accent: string;
    soft: string;
    border: string;
  }
> = {
  offline: {
    label: "Офлайн",
    icon: Users,
    accent: "#ffd400",
    soft: "#fff8cf",
    border: "#111111",
  },
  online: {
    label: "Онлайн",
    icon: Video,
    accent: "#3867D6",
    soft: "#EDF3FF",
    border: "#C8D8FA",
  },
  self_study: {
    label: "Самостійно",
    icon: BookOpen,
    accent: "#111111",
    soft: "#fff8cf",
    border: "#ffd400",
  },
  community: {
    label: "Спільнота",
    icon: Sparkles,
    accent: "#25835C",
    soft: "#EDF8F2",
    border: "#BFE3D0",
  },
  consultation: {
    label: "Консультація",
    icon: Users,
    accent: "#8a6f00",
    soft: "#fff8cf",
    border: "#ffd400",
  },
};

const assignmentTypeLabels: Record<Assignment["type"], string> = {
  course: "Практичне завдання",
  text: "Текстова відповідь",
  file: "Файл",
  link: "Посилання",
  test: "Тест",
};

const submissionStatusLabels = {
  draft: "Чернетка",
  submitted: "На перевірці",
  approved: "Зараховано",
  revision: "Потрібне доопрацювання",
  rejected: "Не зараховано",
};

const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const addDays = (date: Date, amount: number) =>
  new Date(startOfDay(date).getTime() + amount * DAY_MS);

const dateKey = (value: string | Date) => {
  const date = typeof value === "string" ? new Date(value) : value;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(date.getDate()).padStart(2, "0")}`;
};

const startOfWeek = (date: Date) => {
  const day = date.getDay() || 7;
  return addDays(date, 1 - day);
};

const formatTime = (value: string) =>
  new Date(value).toLocaleTimeString("uk-UA", {
    hour: "2-digit",
    minute: "2-digit",
  });

const formatTimeRange = (activity: CalendarActivity) => {
  const start = new Date(activity.startsAt);
  const end = new Date(activity.endsAt);
  if (Number.isNaN(end.getTime()) || end <= start) {
    return formatTime(activity.startsAt);
  }
  return `${formatTime(activity.startsAt)} — ${formatTime(activity.endsAt)}`;
};

const ProgramCalendarPage = () => {
  const [cohort, setCohort] = useState<Cohort | null>(null);
  const [activities, setActivities] = useState<CalendarActivity[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedAssignment, setSelectedAssignment] =
    useState<Assignment | null>(null);
  const [selectedActivity, setSelectedActivity] =
    useState<CalendarActivity | null>(null);
  const [answerText, setAnswerText] = useState("");
  const [answerUrl, setAnswerUrl] = useState("");
  const [answerFiles, setAnswerFiles] = useState<File[]>([]);
  const [submittingAssignment, setSubmittingAssignment] = useState(false);
  const [assignmentMessage, setAssignmentMessage] = useState("");
  const [selectedDate, setSelectedDate] = useState(startOfDay(new Date()));
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [calendarMonth, setCalendarMonth] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  );
  const [calendarCompact, setCalendarCompact] = useState(true);

  const loadCalendar = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await programApi.getMyCalendar();
      const sorted = [...data.activities].sort(
        (left, right) =>
          new Date(left.startsAt).getTime() - new Date(right.startsAt).getTime()
      );
      setCohort(data.cohort);
      setActivities(sorted);
      const sortedAssignments = [...(data.assignments || [])].sort(
        (left, right) =>
          new Date(left.dueAt).getTime() - new Date(right.dueAt).getTime()
      );
      setAssignments(sortedAssignments);
      setSelectedAssignment((selected) => {
        if (!selected) return null;
        const freshAssignment = sortedAssignments.find(
          (item) => item._id === selected._id
        );
        return freshAssignment || null;
      });
      setSelectedActivity((selected) => {
        if (!selected) return null;
        const freshActivity = sorted.find((item) => item._id === selected._id);
        return freshActivity || null;
      });

      if (sorted.length || sortedAssignments.length) {
        const now = Date.now();
        const nearestActivity = sorted.find(
            (activity) =>
              activity.status !== "cancelled" &&
              new Date(activity.endsAt).getTime() >= now
          );
        const nearestAssignment = sortedAssignments.find(
          (assignment) =>
            assignment.mySubmission?.status !== "approved" &&
            new Date(assignment.dueAt).getTime() >= now
        );
        const activityTime = nearestActivity
          ? new Date(nearestActivity.startsAt).getTime()
          : Number.POSITIVE_INFINITY;
        const assignmentTime = nearestAssignment
          ? new Date(nearestAssignment.dueAt).getTime()
          : Number.POSITIVE_INFINITY;
        const nearestDate = startOfDay(
          new Date(
            assignmentTime < activityTime
              ? nearestAssignment!.dueAt
              : nearestActivity?.startsAt ||
                  sortedAssignments[0]?.dueAt ||
                  sorted[0].startsAt
          )
        );
        setSelectedDate(nearestDate);
        setWeekStart(startOfWeek(nearestDate));
        setCalendarMonth(
          new Date(nearestDate.getFullYear(), nearestDate.getMonth(), 1)
        );
      }
    } catch (requestError: any) {
      setError(
        requestError.response?.data?.message ||
          "Не вдалося завантажити календар."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCalendar();
  }, [loadCalendar]);

  useEffect(() => {
    const selectedTime = selectedDate.getTime();
    const weekEndTime = addDays(weekStart, 6).getTime();
    if (selectedTime < weekStart.getTime() || selectedTime > weekEndTime) {
      setWeekStart(startOfWeek(selectedDate));
    }
    if (
      selectedDate.getFullYear() !== calendarMonth.getFullYear() ||
      selectedDate.getMonth() !== calendarMonth.getMonth()
    ) {
      setCalendarMonth(
        new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1)
      );
    }
  }, [calendarMonth, selectedDate, weekStart]);

  const activitiesByDate = useMemo(
    () =>
      activities.reduce<Record<string, CalendarActivity[]>>(
        (result, activity) => {
          const key = dateKey(activity.startsAt);
          result[key] = [...(result[key] || []), activity];
          return result;
        },
        {}
      ),
    [activities]
  );

  const assignmentsByDate = useMemo(
    () =>
      assignments.reduce<Record<string, Assignment[]>>(
        (result, assignment) => {
          const key = dateKey(assignment.dueAt);
          result[key] = [...(result[key] || []), assignment];
          return result;
        },
        {}
      ),
    [assignments]
  );

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, index) => addDays(weekStart, index)),
    [weekStart]
  );

  const selectedActivities = activitiesByDate[dateKey(selectedDate)] || [];
  const selectedAssignments = assignmentsByDate[dateKey(selectedDate)] || [];
  const monthStart = new Date(
    calendarMonth.getFullYear(),
    calendarMonth.getMonth(),
    1
  );
  const mondayOffset = (monthStart.getDay() + 6) % 7;
  const daysInMonth = new Date(
    calendarMonth.getFullYear(),
    calendarMonth.getMonth() + 1,
    0
  ).getDate();
  const calendarCells = [
    ...Array.from({ length: mondayOffset }, () => null),
    ...Array.from({ length: daysInMonth }, (_, index) => index + 1),
  ];

  const nextActivity = useMemo(() => {
    const now = Date.now();
    return (
      activities.find(
        (activity) =>
          activity.status !== "cancelled" &&
          new Date(activity.endsAt).getTime() >= now
      ) || null
    );
  }, [activities]);

  const nextAssignment = useMemo(() => {
    const now = Date.now();
    return (
      assignments.find(
        (assignment) =>
          assignment.mySubmission?.status !== "approved" &&
          new Date(assignment.dueAt).getTime() >= now
      ) || null
    );
  }, [assignments]);

  const completedCount = activities.filter(
    (activity) =>
      activity.status === "completed" ||
      new Date(activity.endsAt).getTime() < Date.now()
  ).length;

  const openNextActivity = () => {
    if (!nextActivity) return;
    const nextDate = startOfDay(new Date(nextActivity.startsAt));
    setSelectedDate(nextDate);
    setWeekStart(startOfWeek(nextDate));
  };

  const moveWeek = (direction: number) => {
    const nextWeek = addDays(weekStart, direction * 7);
    setWeekStart(nextWeek);
    setSelectedDate(nextWeek);
  };

  const goToday = () => {
    const today = startOfDay(new Date());
    setSelectedDate(today);
    setWeekStart(startOfWeek(today));
    setCalendarMonth(new Date(today.getFullYear(), today.getMonth(), 1));
  };

  const changeCalendarMonth = (delta: number) => {
    const nextMonth = new Date(
      calendarMonth.getFullYear(),
      calendarMonth.getMonth() + delta,
      1
    );
    setCalendarMonth(nextMonth);
    setSelectedDate(nextMonth);
    setWeekStart(startOfWeek(nextMonth));
  };

  const openAssignment = (assignment: Assignment) => {
    setSelectedAssignment(assignment);
    setAnswerText(assignment.mySubmission?.answerText || "");
    setAnswerUrl(assignment.mySubmission?.answerUrl || "");
    setAnswerFiles([]);
    setAssignmentMessage("");
  };

  const openActivity = (activity: CalendarActivity) => {
    setSelectedActivity(activity);
  };

  const submitAssignment = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();
    if (!selectedAssignment) return;

    try {
      setSubmittingAssignment(true);
      setAssignmentMessage("");
      await programApi.submitAssignment(selectedAssignment._id, {
        answerText,
        answerUrl,
        files: answerFiles,
        answerData:
          selectedAssignment.type === "test"
            ? { answer: answerText }
            : undefined,
      });
      setAnswerFiles([]);
      setAssignmentMessage("Завдання надіслано на перевірку.");
      await loadCalendar();
    } catch (requestError: any) {
      setAssignmentMessage(
        requestError.response?.data?.message ||
          "Не вдалося надіслати завдання."
      );
    } finally {
      setSubmittingAssignment(false);
    }
  };

  const selectedActivityMeta = selectedActivity
    ? activityMeta[selectedActivity.type]
    : null;
  const SelectedActivityIcon = selectedActivityMeta?.icon || CalendarDays;

  return (
    <section className="min-h-full overflow-x-hidden px-3 py-5 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
      <div className="mx-auto max-w-[1380px]">
        <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full border border-black/10 bg-[#fff8cf] px-3 py-1 text-xs font-black uppercase tracking-wide text-black">
                Моя програма
              </span>
              {cohort && (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-[#DED8CF] bg-white px-3 py-1 text-xs font-semibold text-[#6D665E]">
                  <Users size={13} />
                  {cohort.name}
                </span>
              )}
            </div>
            <h2 className="mt-3 text-[30px] font-extrabold leading-[1.08] tracking-tight text-[#26221E] sm:text-[34px] md:text-4xl">
              Твій тиждень у{" "}
              <span className="notranslate" translate="no">
                МОЖУ
              </span>
            </h2>
            <p className="mt-2 text-sm leading-6 text-[#797169]">
              Усі зустрічі, практики та події потоку в одному ритмі.
            </p>
          </div>
          <button
            type="button"
            onClick={loadCalendar}
            className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl border border-[#DDD7CF] bg-white px-4 text-sm font-semibold text-[#625C55] shadow-sm hover:bg-[#FBFAF8] md:w-auto"
          >
            <RefreshCw size={16} />
            Оновити
          </button>
        </div>

        {error && (
          <div className="mb-5 rounded-xl border border-[#E9BDB5] bg-[#FFF3F1] p-4 text-sm text-[#9D3D2E]">
            {error}
          </div>
        )}

        {loading ? (
          <div className="rounded-2xl border border-[#E3DED6] bg-white p-10 text-[#777068] shadow-sm">
            Завантажуємо твій тиждень...
          </div>
        ) : activities.length === 0 && assignments.length === 0 ? (
          <div className="overflow-hidden rounded-3xl border border-[#E3DED6] bg-white shadow-sm">
            <div className="bg-gradient-to-br from-[#FFF0E8] via-white to-[#EDF3FF] p-10 text-center sm:p-16">
              <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl border-2 border-black bg-[#ffd400] text-black shadow-[5px_5px_0_#000]">
                <CalendarDays size={30} />
              </div>
              <h3 className="mt-5 text-2xl font-bold text-[#302B26]">
                Календар уже готується
              </h3>
              <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-[#7C746C]">
                Координатор формує перші активності потоку. Щойно вони
                з’являться — ти побачиш їх тут.
              </p>
            </div>
          </div>
        ) : (
          <>
            {nextAssignment && (
              <div className="mb-5 flex flex-col gap-4 rounded-2xl border border-[#D9CEF0] bg-gradient-to-r from-[#F8F5FF] to-white p-4 shadow-sm md:flex-row md:items-center md:justify-between md:p-5">
                <div className="flex min-w-0 items-start gap-3 sm:gap-4">
                  <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl border-2 border-black bg-[#ffd400] text-black">
                    <ClipboardList size={22} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs font-black uppercase tracking-wide text-[#8a6f00]">
                        Найближче завдання
                      </span>
                      <span className="rounded-full border border-black/10 bg-white px-2.5 py-1 text-[11px] font-black text-black">
                        {nextAssignment.points} балів
                      </span>
                    </div>
                    <h3 className="mt-1 break-words text-lg font-extrabold leading-snug text-[#302B27]">
                      {nextAssignment.title}
                    </h3>
                    <p className="mt-1 break-words text-sm leading-5 text-[#746C83]">
                      Дедлайн:{" "}
                      {new Date(nextAssignment.dueAt).toLocaleString("uk-UA")}
                      {nextAssignment.mySubmission
                        ? ` · ${nextAssignment.mySubmission.status}`
                        : " · ще не здано"}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => openAssignment(nextAssignment)}
                  className="inline-flex h-11 w-full shrink-0 items-center justify-center gap-2 rounded-2xl border-2 border-black bg-[#ffd400] px-5 text-sm font-black text-black shadow-[4px_4px_0_#000] transition hover:-translate-y-0.5 md:w-auto"
                >
                  Виконати завдання
                  <ArrowRight size={17} />
                </button>
              </div>
            )}

            <div className="mb-6 grid gap-4 xl:grid-cols-[minmax(0,1.6fr)_minmax(300px,0.7fr)]">
              {nextActivity ? (
                <div className="relative overflow-hidden rounded-2xl border border-[#F1CBB9] bg-gradient-to-br from-[#FFF8F3] via-white to-[#FFF0E7] p-5 shadow-lg shadow-orange-100/60 sm:rounded-3xl sm:p-8">
                  <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-[#ffd400]/20 blur-3xl" />
                  <div className="pointer-events-none absolute -bottom-28 left-1/3 h-64 w-64 rounded-full bg-[#F2B544]/10 blur-3xl" />
                  <div className="relative">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full border-2 border-black bg-[#ffd400] px-3 py-1 text-xs font-black uppercase tracking-wider text-black">
                        Наступна активність
                      </span>
                      <span
                        className="rounded-full px-3 py-1 text-xs font-bold"
                        style={{
                          color: activityMeta[nextActivity.type].accent,
                          backgroundColor: activityMeta[nextActivity.type].soft,
                        }}
                      >
                        {activityMeta[nextActivity.type].label}
                      </span>
                    </div>
                    <h3 className="mt-5 max-w-3xl text-2xl font-extrabold text-[#2D2824] sm:text-3xl">
                      {nextActivity.title}
                    </h3>
                    <p className="mt-2 text-sm capitalize text-[#81776F]">
                      {new Date(nextActivity.startsAt).toLocaleDateString(
                        "uk-UA",
                        {
                          weekday: "long",
                          day: "numeric",
                          month: "long",
                        }
                      )}
                    </p>
                    <div className="mt-6 flex flex-wrap gap-3 text-sm">
                      <span className="inline-flex items-center gap-2 rounded-xl border border-[#E9DED6] bg-white px-4 py-2.5 font-semibold text-[#4E4842] shadow-sm">
                        <Clock3 size={17} className="text-[#8a6f00]" />
                        {formatTimeRange(nextActivity)}
                      </span>
                      {nextActivity.location && (
                        <span className="inline-flex items-center gap-2 rounded-xl border border-[#E9DED6] bg-white px-4 py-2.5 text-[#4E4842] shadow-sm">
                          <MapPin size={17} className="text-[#8a6f00]" />
                          {nextActivity.location}
                        </span>
                      )}
                    </div>
                    <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                      <button
                        type="button"
                        onClick={openNextActivity}
                        className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-2xl border-2 border-black bg-[#ffd400] px-5 text-sm font-black text-black shadow-[4px_4px_0_#000] transition hover:-translate-y-0.5 sm:w-auto"
                      >
                        Відкрити в календарі
                        <ArrowRight size={17} />
                      </button>
                      {nextActivity.meetingUrl && (
                        <a
                          href={nextActivity.meetingUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-[#E2C5B7] bg-white px-5 text-sm font-bold text-[#B9471B] hover:bg-[#FFF7F2] sm:w-auto"
                        >
                          Приєднатися
                          <ExternalLink size={16} />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-3xl border border-[#E3DED6] bg-white p-8">
                  <p className="text-sm font-semibold text-[#827A72]">
                    Усі заплановані активності завершено.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-1">
                <div className="rounded-2xl border border-[#E3DED6] bg-white p-5 shadow-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-[#817970]">
                      Всього подій
                    </span>
                    <CalendarDays size={19} className="text-[#8a6f00]" />
                  </div>
                  <p className="mt-4 text-3xl font-extrabold text-[#2D2925]">
                    {activities.length}
                  </p>
                  <p className="mt-1 text-xs text-[#989087]">
                    у календарі потоку
                  </p>
                </div>
                <div className="rounded-2xl border border-[#E3DED6] bg-white p-5 shadow-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-[#817970]">
                      Уже пройдено
                    </span>
                    <Sparkles size={19} className="text-[#25835C]" />
                  </div>
                  <p className="mt-4 text-3xl font-extrabold text-[#2D2925]">
                    {completedCount}
                  </p>
                  <div className="mt-3 h-2 overflow-hidden rounded-full bg-[#EEEAE4]">
                    <div
                      className="h-full rounded-full bg-[#25835C]"
                      style={{
                        width: `${
                          activities.length
                            ? Math.round(
                                (completedCount / activities.length) * 100
                              )
                            : 0
                        }%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-3xl border border-[#E0DBD3] bg-white shadow-sm">
              <div className="grid gap-3 border-b border-[#EEEAE4] bg-[#FCFBF9] px-4 py-4 sm:px-6 lg:grid-cols-[1fr_auto] lg:items-center">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <CalendarDays size={18} className="text-[#8a6f00]" />
                    <h3 className="font-bold text-[#312D28]">
                      Календар потоку
                    </h3>
                  </div>
                  <p className="mt-1 text-sm text-[#817A72]">
                    Активності та дедлайни твого потоку.
                  </p>
                </div>
                <div className="grid gap-2 sm:grid-cols-[auto_auto]">
                  <button
                    type="button"
                    onClick={goToday}
                    className="h-10 rounded-xl border border-[#E1DCD4] bg-white px-4 text-sm font-semibold text-[#665F57] hover:bg-[#F5F2ED]"
                  >
                    Сьогодні
                  </button>
                  <button
                    type="button"
                    onClick={() => setCalendarCompact((current) => !current)}
                    className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border-2 border-black bg-white px-4 text-sm font-black text-black shadow-[3px_3px_0_#ffd400] transition hover:-translate-y-0.5"
                  >
                    {calendarCompact ? (
                      <ChevronDown size={17} />
                    ) : (
                      <ChevronUp size={17} />
                    )}
                    {calendarCompact ? "Розгорнути" : "Компактно"}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3 border-b border-[#EEEAE4] px-3 py-3 sm:px-5">
                <div className="flex items-center justify-between gap-2 sm:justify-start sm:gap-3">
                  <button
                    type="button"
                    onClick={() =>
                      calendarCompact ? moveWeek(-1) : changeCalendarMonth(-1)
                    }
                    className="grid h-9 w-9 place-items-center rounded-xl border border-[#E1DCD4] bg-white text-[#665F57] hover:bg-[#F5F2ED]"
                    aria-label="Попередній період"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <div className="min-w-0 flex-1 text-center sm:min-w-[170px] sm:flex-none">
                    <p className="text-sm font-bold capitalize text-[#37322D]">
                      {(calendarCompact ? weekStart : calendarMonth).toLocaleDateString(
                        "uk-UA",
                        {
                          month: "long",
                          year: "numeric",
                        }
                      )}
                    </p>
                    <p className="text-xs text-[#948C84]">
                      {calendarCompact ? "План на тиждень" : "План на місяць"}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      calendarCompact ? moveWeek(1) : changeCalendarMonth(1)
                    }
                    className="grid h-9 w-9 place-items-center rounded-xl border border-[#E1DCD4] bg-white text-[#665F57] hover:bg-[#F5F2ED]"
                    aria-label="Наступний період"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
                <div className="flex items-center gap-3 text-xs font-semibold text-[#817A72]">
                  <span className="inline-flex items-center gap-1.5">
                    <i className="h-2.5 w-2.5 rounded-full bg-[#ffd400] ring-1 ring-black/20" />
                    Активність
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <i className="h-2.5 w-2.5 rounded-full bg-black" />
                    Завдання
                  </span>
                </div>
              </div>

              {calendarCompact ? (
                <div className="-mx-px overflow-x-auto px-3 py-4 sm:px-5">
                  <div className="flex min-w-max gap-2 sm:grid sm:min-w-[720px] sm:grid-cols-7">
                    {weekDays.map((day) => {
                      const key = dateKey(day);
                      const activityCount = activitiesByDate[key]?.length || 0;
                      const assignmentCount = assignmentsByDate[key]?.length || 0;
                      const count = activityCount + assignmentCount;
                      const selected = key === dateKey(selectedDate);
                      const today = key === dateKey(new Date());

                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setSelectedDate(day)}
                          className={`relative w-[72px] shrink-0 rounded-2xl border px-2 py-3 text-center transition sm:w-auto ${
                            selected
                              ? "border-black bg-[#fff8cf] shadow-[4px_4px_0_#ffd400]"
                              : "border-transparent hover:border-[#E5E0D8] hover:bg-[#FAF8F5]"
                          }`}
                        >
                          <p
                            className={`text-xs font-bold uppercase ${
                              selected ? "text-black" : "text-[#958E85]"
                            }`}
                          >
                            {day
                              .toLocaleDateString("uk-UA", { weekday: "short" })
                              .replace(".", "")}
                          </p>
                          <div
                            className={`mx-auto mt-2 grid h-10 w-10 place-items-center rounded-xl text-lg font-extrabold ${
                              selected
                                ? "bg-[#ffd400] text-black border-2 border-black"
                                : today
                                ? "bg-[#27231F] text-white"
                                : "text-[#37322D]"
                            }`}
                          >
                            {day.getDate()}
                          </div>
                          <div className="mt-2 flex h-2 items-center justify-center gap-1">
                            {Array.from({ length: Math.min(count, 3) }).map(
                              (_, index) => (
                                <span
                                  key={index}
                                  className={`h-1.5 w-1.5 rounded-full ${
                                    index >= activityCount
                                      ? "bg-black"
                                      : "bg-[#ffd400]"
                                  }`}
                                />
                              )
                            )}
                            {count > 3 && (
                              <span className="text-[9px] font-black text-black">
                                +{count - 3}
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="w-full overflow-hidden">
                  <div className="grid grid-cols-7 border-b border-[#EEEAE4] bg-[#FAF9F7]">
                    {["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Нд"].map((day) => (
                      <div
                        key={day}
                        className="px-1 py-2 text-center text-[10px] font-bold uppercase text-[#817A72] sm:px-2 sm:py-3 sm:text-xs"
                      >
                        {day}
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7">
                    {calendarCells.map((day, index) => {
                      const dayDate = day
                        ? new Date(
                            calendarMonth.getFullYear(),
                            calendarMonth.getMonth(),
                            day
                          )
                        : null;
                      const key = dayDate ? dateKey(dayDate) : "";
                      const dayActivities = dayDate ? activitiesByDate[key] || [] : [];
                      const dayAssignments = dayDate ? assignmentsByDate[key] || [] : [];
                      const entries = [
                        ...dayActivities.map((activity) => ({
                          id: activity._id,
                          kind: "activity" as const,
                          title: activity.title,
                        })),
                        ...dayAssignments.map((assignment) => ({
                          id: assignment._id,
                          kind: "assignment" as const,
                          title: assignment.title,
                        })),
                      ];
                      const selected = key === dateKey(selectedDate);
                      const today = key === dateKey(new Date());

                      return (
                        <button
                          key={day ? key : `empty-${index}`}
                          type="button"
                          disabled={!dayDate}
                          onClick={() => dayDate && setSelectedDate(dayDate)}
                          className={`min-h-[94px] min-w-0 border-b border-r border-[#EEEAE4] p-1.5 text-left align-top transition sm:min-h-[128px] sm:p-2 ${
                            !dayDate
                              ? "bg-[#FAF9F7]"
                              : selected
                              ? "bg-[#fff8cf]"
                              : today
                              ? "bg-[#fffdf2]"
                              : "bg-white hover:bg-[#FAF9F7]"
                          }`}
                        >
                          {dayDate && (
                            <>
                              <div className="flex items-center justify-between gap-1">
                                <span
                                  className={`grid h-7 w-7 place-items-center rounded-lg text-xs font-black sm:h-8 sm:w-8 sm:text-sm ${
                                    selected
                                      ? "border-2 border-black bg-[#ffd400] text-black"
                                      : today
                                      ? "bg-black text-[#ffd400]"
                                      : "text-black"
                                  }`}
                                >
                                  {dayDate.getDate()}
                                </span>
                                {entries.length > 0 && (
                                  <span className="rounded-full bg-[#F7F4EF] px-1.5 py-0.5 text-[10px] font-black text-black">
                                    {entries.length}
                                  </span>
                                )}
                              </div>
                              <div className="mt-2 space-y-1">
                                {entries.slice(0, 3).map((entry) => (
                                  <span
                                    key={`${entry.kind}-${entry.id}`}
                                    className={`block truncate rounded-lg px-1.5 py-1 text-[10px] font-bold sm:text-xs ${
                                      entry.kind === "assignment"
                                        ? "bg-black text-[#ffd400]"
                                        : "bg-[#ffd400] text-black"
                                    }`}
                                  >
                                    {entry.title}
                                  </span>
                                ))}
                                {entries.length > 3 && (
                                  <span className="block text-[10px] font-black text-[#817A72]">
                                    +{entries.length - 3} ще
                                  </span>
                                )}
                              </div>
                            </>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="border-t border-[#EEEAE4] p-4 sm:p-7">
                <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                  <div>
                    <p className="text-xs font-black uppercase tracking-wide text-[#8a6f00]">
                      Обраний день
                    </p>
                    <h3 className="mt-1 text-2xl font-extrabold capitalize text-[#2E2925]">
                      {selectedDate.toLocaleDateString("uk-UA", {
                        weekday: "long",
                        day: "numeric",
                        month: "long",
                      })}
                    </h3>
                  </div>
                  <p className="text-sm leading-5 text-[#8B837B] md:text-right">
                    {selectedActivities.length + selectedAssignments.length
                      ? `${selectedActivities.length} активностей · ${selectedAssignments.length} завдань`
                      : "Вільний день"}
                  </p>
                </div>

                {selectedActivities.length === 0 &&
                selectedAssignments.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-[#DAD4CB] bg-[#FAF9F7] p-10 text-center">
                    <div className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-white text-[#B0A89F] shadow-sm">
                      <Laptop size={22} />
                    </div>
                    <h4 className="mt-4 font-bold text-[#4A443E]">
                      Сьогодні без активностей
                    </h4>
                    <p className="mt-1 text-sm text-[#8B837B]">
                      Гарний момент закріпити матеріал або трохи відпочити.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {selectedAssignments.length > 0 && (
                      <div>
                        <p className="mb-3 text-xs font-black uppercase tracking-wide text-black">
                          Дедлайни завдань
                        </p>
                        <div className="space-y-3">
                          {selectedAssignments.map((assignment) => {
                            const submission = assignment.mySubmission;
                            const overdue =
                              new Date(assignment.dueAt).getTime() < Date.now() &&
                              submission?.status !== "approved";
                            return (
                              <button
                                key={assignment._id}
                                type="button"
                                onClick={() => openAssignment(assignment)}
                                className={`flex w-full flex-col gap-4 rounded-2xl border p-4 text-left transition hover:-translate-y-0.5 hover:shadow-lg md:flex-row md:items-center md:justify-between md:p-5 ${
                                  overdue
                                    ? "border-[#E9BDB5] bg-[#FFF7F5]"
                                    : "border-[#D9CEF0] bg-[#FBF9FF]"
                                }`}
                              >
                                <div className="flex min-w-0 items-start gap-3 sm:gap-4">
                                  <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-black/10 bg-[#fff8cf] text-black">
                                    <ClipboardList size={20} />
                                  </div>
                                  <div className="min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <span className="text-xs font-black text-black">
                                        Тиждень {assignment.weekNumber}
                                      </span>
                                      <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-bold text-[#8A4A2E]">
                                        {assignment.points} балів
                                      </span>
                                    </div>
                                    <h4 className="mt-1 break-words text-lg font-extrabold leading-snug text-[#302B27]">
                                      {assignment.title}
                                    </h4>
                                    <p className="mt-1 break-words text-xs leading-5 text-[#716A62]">
                                      Дедлайн о{" "}
                                      {formatTime(assignment.dueAt)}
                                      {submission
                                        ? ` · ${submission.status}`
                                        : " · не здано"}
                                    </p>
                                  </div>
                                </div>
                                <span className="inline-flex h-10 w-full items-center justify-center rounded-2xl border-2 border-black bg-[#ffd400] px-4 text-sm font-black text-black md:w-auto md:shrink-0">
                                  Відкрити
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {selectedActivities.length > 0 && (
                      <div>
                        <p className="mb-3 text-xs font-black uppercase tracking-wide text-[#8a6f00]">
                          Активності
                        </p>
                        <div className="space-y-4">
                    {selectedActivities.map((activity, index) => {
                      const meta = activityMeta[activity.type];
                      const Icon = meta.icon;

                      return (
                        <article
                          key={activity._id}
                          role="button"
                          tabIndex={0}
                          onClick={() => openActivity(activity)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              openActivity(activity);
                            }
                          }}
                          className={`group relative grid cursor-pointer gap-4 rounded-2xl border p-4 text-left transition md:grid-cols-[92px_minmax(0,1fr)_auto] md:items-center md:p-5 ${
                            activity.status === "cancelled"
                              ? "opacity-55"
                              : "hover:-translate-y-0.5 hover:shadow-lg hover:shadow-black/5"
                          }`}
                          style={{ borderColor: meta.border }}
                        >
                          {index < selectedActivities.length - 1 && (
                            <span
                              className="absolute left-[65px] top-[76px] hidden h-[calc(100%_-_42px)] w-px md:block"
                              style={{ backgroundColor: meta.border }}
                            />
                          )}
                          <div>
                            <p className="text-xl font-extrabold text-[#332E29]">
                              {formatTime(activity.startsAt)}
                            </p>
                            <p className="mt-1 text-xs text-[#918981]">
                              {formatTimeRange(activity).includes("—")
                                ? `до ${formatTime(activity.endsAt)}`
                                : "час старту"}
                            </p>
                          </div>

                          <div className="flex min-w-0 items-start gap-4">
                            <div
                              className="grid h-11 w-11 shrink-0 place-items-center rounded-xl"
                              style={{
                                color: meta.accent,
                                backgroundColor: meta.soft,
                              }}
                            >
                              <Icon size={20} />
                            </div>
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <span
                                  className="text-xs font-bold"
                                  style={{ color: meta.accent }}
                                >
                                  {meta.label}
                                </span>
                                {activity.status === "cancelled" && (
                                  <span className="rounded-full bg-[#FCE8E5] px-2 py-0.5 text-[10px] font-bold text-[#9D3D2E]">
                                    Скасовано
                                  </span>
                                )}
                              </div>
                              <h4 className="mt-1 truncate text-lg font-extrabold text-[#302B27]">
                                {activity.title}
                              </h4>
                              {activity.description && (
                                <p className="mt-1 line-clamp-2 text-sm leading-6 text-[#7C746C]">
                                  {activity.description}
                                </p>
                              )}
                              <div className="mt-2 flex flex-wrap gap-3 text-xs text-[#716A62]">
                                {activity.location && (
                                  <span className="inline-flex items-center gap-1.5">
                                    <MapPin size={14} style={{ color: meta.accent }} />
                                    {activity.location}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          {activity.meetingUrl ? (
                            <a
                              href={activity.meetingUrl}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(event) => event.stopPropagation()}
                              className="inline-flex h-10 items-center justify-center gap-2 rounded-xl px-4 text-sm font-bold text-white"
                              style={{ backgroundColor: meta.accent }}
                            >
                              Приєднатися
                              <ExternalLink size={15} />
                            </a>
                          ) : (
                            <div
                              className="hidden h-10 w-10 place-items-center rounded-xl md:grid"
                              style={{
                                color: meta.accent,
                                backgroundColor: meta.soft,
                              }}
                            >
                              <ArrowRight size={18} />
                            </div>
                          )}
                        </article>
                      );
                    })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-6 grid grid-cols-2 gap-3 border-t border-[#EEEAE4] pt-5">
                  <button
                    type="button"
                    onClick={() => setSelectedDate(addDays(selectedDate, -1))}
                    className="inline-flex items-center justify-start gap-2 text-left text-xs font-bold text-[#6F6860] hover:text-black sm:text-sm"
                  >
                    <ArrowLeft size={16} />
                    Попередній день
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedDate(addDays(selectedDate, 1))}
                    className="inline-flex items-center justify-end gap-2 text-right text-xs font-bold text-[#6F6860] hover:text-black sm:text-sm"
                  >
                    Наступний день
                    <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {selectedActivity && selectedActivityMeta && (
          <div
            className="fixed inset-0 z-[100] flex items-end justify-center bg-black/45 p-0 backdrop-blur-sm sm:items-center sm:p-5"
            role="dialog"
            aria-modal="true"
          >
            <button
              type="button"
              className="absolute inset-0 cursor-default"
              onClick={() => setSelectedActivity(null)}
              aria-label="Закрити"
            />
            <div className="relative max-h-[94dvh] w-full max-w-4xl overflow-hidden rounded-t-[28px] border-2 border-black bg-white shadow-[8px_8px_0_#ffd400] sm:max-h-[92vh] sm:rounded-[30px]">
              <div className="relative overflow-hidden bg-[#080808] px-4 py-5 text-white sm:px-7 sm:py-7">
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_16%_20%,rgba(255,212,0,0.28),transparent_30%),repeating-linear-gradient(135deg,rgba(255,212,0,0.14)_0_2px,transparent_2px_14px)]" />
                <div className="relative flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className="inline-flex items-center gap-2 rounded-full border border-white/20 px-3 py-1 text-xs font-black uppercase tracking-wide"
                        style={{
                          color: selectedActivityMeta.accent,
                          backgroundColor: "rgba(255,255,255,0.08)",
                        }}
                      >
                        <SelectedActivityIcon size={14} />
                        {selectedActivityMeta.label}
                      </span>
                      {selectedActivity.status === "cancelled" && (
                        <span className="rounded-full bg-[#FCE8E5] px-3 py-1 text-xs font-black text-[#9D3D2E]">
                          Скасовано
                        </span>
                      )}
                    </div>
                    <h3 className="mt-4 break-words text-3xl font-black uppercase leading-none tracking-[-0.05em] text-white sm:text-5xl">
                      {selectedActivity.title}
                    </h3>
                    {cohort && (
                      <p className="mt-3 text-sm font-semibold text-white/68">
                        {cohort.name} · {cohort.code}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedActivity(null)}
                    className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl border-2 border-black bg-[#ffd400] text-black shadow-[3px_3px_0_#000]"
                    aria-label="Закрити активність"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              <div className="max-h-[calc(94dvh-170px)] overflow-y-auto px-4 py-5 sm:max-h-[calc(92vh-210px)] sm:px-7 sm:py-7">
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <div className="rounded-2xl border border-[#E8E1D8] bg-[#fffdf7] p-4">
                    <CalendarDays size={18} className="text-[#8a6f00]" />
                    <p className="mt-3 text-xs font-black uppercase tracking-wide text-[#8a6f00]">
                      Дата
                    </p>
                    <p className="mt-1 text-sm font-black capitalize text-black">
                      {new Date(selectedActivity.startsAt).toLocaleDateString(
                        "uk-UA",
                        {
                          weekday: "long",
                          day: "numeric",
                          month: "long",
                        }
                      )}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-[#E8E1D8] bg-[#fffdf7] p-4">
                    <Clock3 size={18} className="text-[#8a6f00]" />
                    <p className="mt-3 text-xs font-black uppercase tracking-wide text-[#8a6f00]">
                      Час
                    </p>
                    <p className="mt-1 text-sm font-black text-black">
                      {formatTimeRange(selectedActivity)}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-[#E8E1D8] bg-[#fffdf7] p-4">
                    <SelectedActivityIcon
                      size={18}
                      style={{ color: selectedActivityMeta.accent }}
                    />
                    <p className="mt-3 text-xs font-black uppercase tracking-wide text-[#8a6f00]">
                      Формат
                    </p>
                    <p className="mt-1 text-sm font-black text-black">
                      {selectedActivityMeta.label}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-[#E8E1D8] bg-[#fffdf7] p-4">
                    <MapPin size={18} className="text-[#8a6f00]" />
                    <p className="mt-3 text-xs font-black uppercase tracking-wide text-[#8a6f00]">
                      Місце
                    </p>
                    <p className="mt-1 break-words text-sm font-black text-black">
                      {selectedActivity.location || "Буде уточнено"}
                    </p>
                  </div>
                </div>

                <div className="mt-5 rounded-3xl border border-[#E8E1D8] bg-[#f8fafc] p-5 sm:p-6">
                  <p className="text-xs font-black uppercase tracking-[0.16em] text-[#8a6f00]">
                    Про активність
                  </p>
                  <p className="mt-3 whitespace-pre-line break-words text-sm font-semibold leading-7 text-[#625C55] sm:text-base">
                    {selectedActivity.description ||
                      "Детальний опис активності координатор додасть окремо."}
                  </p>
                </div>

                <div className="mt-5">
                  <ProgramMaterials
                    materials={selectedActivity.materials}
                    title="Матеріали активності"
                  />
                </div>

                <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                  {selectedActivity.meetingUrl && (
                    <a
                      href={selectedActivity.meetingUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-2xl border-2 border-black bg-[#ffd400] px-5 text-sm font-black text-black shadow-[4px_4px_0_#000] transition hover:-translate-y-0.5 sm:w-auto"
                    >
                      Приєднатися / відкрити посилання
                      <ExternalLink size={17} />
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={() => setSelectedActivity(null)}
                    className="inline-flex h-12 w-full items-center justify-center rounded-2xl border-2 border-black/10 bg-white px-5 text-sm font-black text-[#625C55] transition hover:border-black sm:w-auto"
                  >
                    Закрити
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {selectedAssignment && (
          <div
            className="fixed inset-0 z-[100] flex items-end justify-center bg-black/45 p-0 backdrop-blur-sm sm:items-center sm:p-5"
            role="dialog"
            aria-modal="true"
          >
            <button
              type="button"
              className="absolute inset-0 cursor-default"
              onClick={() => setSelectedAssignment(null)}
              aria-label="Закрити"
            />
            <div className="relative max-h-[94dvh] w-full max-w-4xl overflow-hidden rounded-t-[28px] border-2 border-black bg-white shadow-[8px_8px_0_#ffd400] sm:max-h-[92vh] sm:rounded-[30px]">
              <div className="relative overflow-hidden bg-[#080808] px-4 py-5 text-white sm:px-7 sm:py-7">
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_16%_20%,rgba(255,212,0,0.28),transparent_30%),repeating-linear-gradient(135deg,rgba(255,212,0,0.14)_0_2px,transparent_2px_14px)]" />
                <div className="relative flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-black uppercase tracking-wide text-[#ffd400]">
                        <ClipboardList size={14} />
                        Тиждень {selectedAssignment.weekNumber}
                      </span>
                      <span className="rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-black uppercase tracking-wide text-white">
                        {assignmentTypeLabels[selectedAssignment.type]}
                      </span>
                    </div>
                    <h3 className="mt-4 break-words text-3xl font-black uppercase leading-none tracking-[-0.05em] text-white sm:text-5xl">
                      {selectedAssignment.title}
                    </h3>
                    <p className="mt-3 text-sm font-semibold text-white/68">
                      Завдання потоку
                      {cohort ? ` · ${cohort.name}` : ""}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedAssignment(null)}
                    className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl border-2 border-black bg-[#ffd400] text-black shadow-[3px_3px_0_#000]"
                    aria-label="Закрити завдання"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              <div className="max-h-[calc(94dvh-170px)] overflow-y-auto px-4 py-5 sm:max-h-[calc(92vh-210px)] sm:px-7 sm:py-7">
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <div className="rounded-2xl border border-[#E8E1D8] bg-[#fffdf7] p-4">
                    <Clock3 size={18} className="text-[#8a6f00]" />
                    <p className="mt-3 text-xs font-black uppercase tracking-wide text-[#8a6f00]">
                      Дедлайн
                    </p>
                    <p className="mt-1 text-sm font-black text-black">
                      {new Date(selectedAssignment.dueAt).toLocaleString(
                        "uk-UA"
                      )}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-[#E8E1D8] bg-[#fffdf7] p-4">
                    <Sparkles size={18} className="text-[#8a6f00]" />
                    <p className="mt-3 text-xs font-black uppercase tracking-wide text-[#8a6f00]">
                      Бали
                    </p>
                    <p className="mt-1 text-sm font-black text-black">
                      {selectedAssignment.points}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-[#E8E1D8] bg-[#fffdf7] p-4">
                    <BookOpen size={18} className="text-[#8a6f00]" />
                    <p className="mt-3 text-xs font-black uppercase tracking-wide text-[#8a6f00]">
                      Тип
                    </p>
                    <p className="mt-1 text-sm font-black text-black">
                      {assignmentTypeLabels[selectedAssignment.type]}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-[#E8E1D8] bg-[#fffdf7] p-4">
                    <ClipboardList size={18} className="text-[#8a6f00]" />
                    <p className="mt-3 text-xs font-black uppercase tracking-wide text-[#8a6f00]">
                      Статус
                    </p>
                    <p className="mt-1 text-sm font-black text-black">
                      {selectedAssignment.mySubmission
                        ? submissionStatusLabels[
                            selectedAssignment.mySubmission.status
                          ]
                        : "Ще не здано"}
                    </p>
                  </div>
                </div>

                <div className="mt-5 rounded-3xl border border-[#E8E1D8] bg-[#f8fafc] p-5 sm:p-6">
                  <p className="text-xs font-black uppercase tracking-[0.16em] text-[#8a6f00]">
                    Що потрібно зробити
                  </p>
                  <p className="mt-3 whitespace-pre-line break-words text-sm font-semibold leading-7 text-[#625C55] sm:text-base">
                    {selectedAssignment.description ||
                      "Опис завдання координатор додасть окремо."}
                  </p>
                </div>

                <div className="mt-5">
                  <ProgramMaterials
                    materials={selectedAssignment.materials}
                    title="Матеріали завдання"
                  />
                </div>

                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  {selectedAssignment.materialUrl && (
                    <a
                      href={selectedAssignment.materialUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border-2 border-black bg-[#ffd400] px-5 text-sm font-black text-black shadow-[4px_4px_0_#000] transition hover:-translate-y-0.5"
                    >
                      Відкрити матеріали
                      <ExternalLink size={17} />
                    </a>
                  )}
                  {selectedAssignment.mySubmission && (
                    <div className="rounded-2xl border border-[#E8E1D8] bg-[#fffdf7] px-4 py-3">
                      <p className="text-xs font-black uppercase tracking-wide text-[#8a6f00]">
                        Твоя відповідь
                      </p>
                      <p className="mt-1 text-sm font-black text-black">
                        {
                          submissionStatusLabels[
                            selectedAssignment.mySubmission.status
                          ]
                        }
                        {selectedAssignment.mySubmission.status === "approved"
                          ? ` · ${selectedAssignment.mySubmission.awardedPoints} балів`
                          : ""}
                      </p>
                    </div>
                  )}
                </div>

                {selectedAssignment.mySubmission?.adminComment && (
                  <div className="mt-5 rounded-2xl border border-[#F1D79B] bg-[#FFF8E8] p-5">
                    <p className="text-xs font-black uppercase tracking-wide text-[#9B6A18]">
                      Коментар адміністратора
                    </p>
                    <p className="mt-2 whitespace-pre-line break-words text-sm font-semibold leading-6 text-[#6D572D]">
                      {selectedAssignment.mySubmission.adminComment}
                    </p>
                  </div>
                )}

                {selectedAssignment.mySubmission?.files?.length ? (
                  <div className="mt-5">
                    <ProgramMaterials
                      materials={selectedAssignment.mySubmission.files}
                      title="Твої прикріплені файли"
                    />
                  </div>
                ) : null}

                <form
                  onSubmit={submitAssignment}
                  className="mt-6 space-y-5 rounded-3xl border border-[#E8E1D8] bg-white p-5 sm:p-6"
                >
                  <div>
                    <p className="text-xs font-black uppercase tracking-[0.16em] text-[#8a6f00]">
                      Здати завдання
                    </p>
                    <p className="mt-1 text-sm font-semibold text-[#817A72]">
                      Додай відповідь або посилання на результат, щоб команда
                      могла перевірити роботу.
                    </p>
                  </div>
                  {["text", "course", "test"].includes(
                    selectedAssignment.type
                  ) && (
                    <label className="block">
                      <span className="text-sm font-semibold text-[#4E4943]">
                        Ваша відповідь
                      </span>
                      <textarea
                        rows={7}
                        value={answerText}
                        onChange={(event) => setAnswerText(event.target.value)}
                        className="mt-2 w-full rounded-2xl border-2 border-black/10 px-4 py-3 outline-none focus:border-black focus:ring-4 focus:ring-[#ffd400]/30"
                      />
                    </label>
                  )}

                  {["file", "link", "course"].includes(
                    selectedAssignment.type
                  ) && (
                    <label className="block">
                      <span className="text-sm font-semibold text-[#4E4943]">
                        Посилання на результат або файл
                      </span>
                      <input
                        type="url"
                        value={answerUrl}
                        onChange={(event) => setAnswerUrl(event.target.value)}
                        className="mt-2 h-12 w-full rounded-2xl border-2 border-black/10 px-4 outline-none focus:border-black focus:ring-4 focus:ring-[#ffd400]/30"
                      />
                    </label>
                  )}

                  <div className="rounded-3xl border border-[#E8E1D8] bg-[#fffdf7] p-4">
                    <p className="text-sm font-black text-black">
                      Файли до відповіді
                    </p>
                    <p className="mt-1 text-xs font-semibold text-[#817A72]">
                      Можна прикріпити документи, фото, PDF або архіви.
                    </p>
                    <div className="mt-3">
                      <MaterialDropzone
                        files={answerFiles}
                        onFilesChange={setAnswerFiles}
                      />
                    </div>
                  </div>

                  {assignmentMessage && (
                    <div className="rounded-xl border border-[#E5D2C5] bg-[#FFF8F3] px-4 py-3 text-sm font-semibold text-[#8A4A2E]">
                      {assignmentMessage}
                    </div>
                  )}

                  {selectedAssignment.mySubmission?.status === "approved" ? (
                    <div className="rounded-xl border border-[#BFE3D0] bg-[#EDF8F2] p-4 font-semibold text-[#25835C]">
                      Завдання зараховано:{" "}
                      {selectedAssignment.mySubmission.awardedPoints} балів
                    </div>
                  ) : (
                    <button
                      disabled={submittingAssignment}
                      className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-2xl border-2 border-black bg-[#ffd400] px-5 text-sm font-black text-black shadow-[4px_4px_0_#000] disabled:opacity-60 sm:w-auto"
                    >
                      <Send size={17} />
                      {submittingAssignment
                        ? "Надсилаємо..."
                        : "Надіслати на перевірку"}
                    </button>
                  )}
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProgramCalendarPage;
